// @ts-strict-ignore
export class ArrayUtils {
  static union(...arrays: any[][]): any[] {
    return [...new Set([].concat(...arrays))].sort(
      (item1, item2) =>
        ArrayUtils.getMinIndexOf(item1, arrays) - ArrayUtils.getMinIndexOf(item2, arrays),
    )
  }

  private static getMinIndexOf(item, arrays: any[][]): number {
    return arrays.reduce((index: number, array: any[]) => {
      const currentIndex = array.indexOf(item)
      if (currentIndex >= 0 && (index < 0 || currentIndex < index)) {
        return currentIndex
      }
      return index
    }, -1)
  }

  static sortBy(property) {
    return function (a, b) {
      if (a[property] < b[property]) {
        return -1
      }
      return a[property] > b[property] ? 1 : 0
    }
  }

  static filterExistingItems<T, K extends keyof T>(
    baseArray: T[],
    items: T[],
    key?: K,
  ): { newItems: T[]; duplicatedItems: T[] } {
    const getItemId = (item: T): T | T[K] =>
      typeof item === 'object' && item !== null && key in item ? item[key] : item

    const baseArrayIDs = new Set()
    for (const item of baseArray) {
      baseArrayIDs.add(getItemId(item))
    }
    const newItems: T[] = []
    const duplicatedItems: T[] = []
    for (const item of items) {
      const isNewItem = !baseArrayIDs.has(getItemId(item))
      if (isNewItem) {
        baseArrayIDs.add(getItemId(item))
        newItems.push(item)
      } else {
        duplicatedItems.push(item)
      }
    }

    return { newItems, duplicatedItems }
  }

  static addUniqueItems<T, K extends keyof T>(baseArray: T[], items: T[], key?: K): T[] {
    const { newItems } = this.filterExistingItems<T, K>(baseArray, items, key)
    return baseArray.concat(newItems)
  }

  static removeDuplicates<T, K extends keyof T>(array: T[], key?: K): T[] {
    if (array.length < 2) {
      return array
    }
    if (typeof array[0] === 'object' && key) {
      const uniqueIds = new Set()
      return array.filter((obj: T) => !uniqueIds.has(obj[key]) && uniqueIds.add(obj[key]))
    } else {
      return Array.from(new Set(array))
    }
  }

  static getIntersection<T>(array1: T[], array2: T[]): T[] {
    return this.removeDuplicates(array1.filter(value => array2.includes(value)))
  }

  static findInHierarchy<Type extends { children?: Array<Type> }>(
    value: string | number,
    array: Array<Type>,
    valueField = 'id',
  ): Array<Type> {
    const result: Array<Type> = []
    for (const item of array) {
      if (item[valueField] === value) {
        result.push(item)
        break
      } else if (item.children?.length) {
        const partialResult = ArrayUtils.findInHierarchy(value, item.children, valueField)
        if (partialResult.length) {
          result.push(item, ...partialResult)
          break
        }
      }
    }
    return result
  }

  static transformArrayToString(array: Array<any>, separator: string): string {
    return array.join(separator)
  }

  static formatArrayToString(
    array: Array<any>,
    emptyArrayValue: string,
    separator: string,
  ): string {
    if (array.length === 0) {
      return emptyArrayValue
    }
    return this.transformArrayToString(array, separator)
  }
}
